export const en = {
  // Navigation
  nav: {
    home: "Home",
    features: "Features",
    pricing: "Pricing",
    about: "About",
    blog: "Blog",
    download: "Download",
    contact: "Contact",
    signIn: "Sign In",
    getStarted: "Get Started",
    signOut: "Sign Out",
    settings: "Settings",
    dashboard: "Dashboard",
    back: "Back",
  },

  // Common
  common: {
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    yes: "Yes",
    no: "No",
    ok: "OK",
    close: "Close",
    submit: "Submit",
    sending: "Sending...",
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Info",
    confirm: "Confirm",
    retry: "Retry",
    readMore: "Read more",
    learnMore: "Learn More",
    viewAll: "View All",
    seeAll: "See All",
    noData: "No data",
    optional: "Optional",
    required: "Required",
    all: "All",
    none: "None",
    total: "Total",
    date: "Date",
    name: "Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    description: "Description",
    notes: "Notes",
    status: "Status",
    actions: "Actions",
    amount: "Amount",
    balance: "Balance",
    type: "Type",
    category: "Category",
    price: "Price",
    quantity: "Quantity",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    changesAutoSaved: "Changes are saved automatically",
    free: "Free",
    forever: "Forever",
    copyright: "© 2025 FINO. All rights reserved.",
    financesAtFingertips: "Finances at your fingertips",
  },

  // Home page
  home: {
    heroTag: "Two Modes in One Powerful App — 100% Free",
    heroTitle: "Your Complete",
    heroTitleHighlight: "Finance & Business",
    heroTitleEnd: "Solution",
    heroDescription: "Whether you're tracking personal expenses or managing a full retail shop — FINO has you covered. Personal cashbook or complete inventory management, all in one app.",
    getStartedFree: "Get Started Free",
    seeAllFeatures: "See All Features",
    twoModesTitle: "Two Modes, One Powerful App",
    twoModesDescription: "Choose the mode that fits your needs. Switch anytime with just one click.",
    cashbookMode: "Cashbook Mode",
    cashbookSubtitle: "Personal Finance Tracking",
    cashbookDescription: "Perfect for individuals, freelancers, and households who want to track their daily income and expenses without any complexity.",
    perfectFor: "Perfect For:",
    individualsAndFamilies: "Individuals & Families",
    freelancersAndConsultants: "Freelancers & Consultants",
    smallServiceProviders: "Small Service Providers",
    keyFeatures: "Key Features:",
    incomeTracking: "Income Tracking",
    expenseLogging: "Expense Logging",
    multipleCashbooks: "Multiple Cashbooks",
    smartAnalytics: "Smart Analytics",
    cloudBackup: "Cloud Backup",
    reportsAndExport: "Reports & Export",
    imsMode: "IMS / POS Mode",
    imsSubtitle: "Inventory & Point of Sale",
    imsDescription: "Complete business solution for shops, wholesalers, and distributors. Manage inventory, customers, vendors, and sales — all in one place.",
    retailShops: "Retail Shops & Stores",
    wholesalers: "Wholesalers & Distributors",
    smbBusinesses: "Small & Medium Businesses",
    productCatalog: "Product Catalog",
    customerLedger: "Customer Ledger",
    vendorManagement: "Vendor Management",
    invoiceGeneration: "Invoice Generation",
    paymentReminders: "Payment Reminders",
    smartPricing: "Smart Pricing",
    switchModesNote: "Not sure which mode is right for you? Don't worry — you can switch between modes anytime!",
    exploreAllFeatures: "Explore All Features",
    imsHighlightsTitle: "Powerful IMS Features — All Free",
    imsHighlightsDescription: "Running a business? FINO gives you enterprise-level features without the enterprise price tag.",
    paymentRemindersDesc: "Never miss a payment. Get notified when customers owe you money.",
    smartPricingDesc: "Auto rate suggestions based on purchase history and profit margins.",
    batchTracking: "Batch Tracking",
    batchTrackingDesc: "Track products batch-by-batch with purchase rates and expiry dates.",
    expiryAlerts: "Expiry Alerts",
    expiryAlertsDesc: "Get notified before products expire. No more losses from expired stock.",
    professionalInvoices: "Professional Invoices",
    professionalInvoicesDesc: "Generate PDF invoices with your logo. Share instantly via WhatsApp.",
    customerLedgers: "Customer Ledgers",
    customerLedgersDesc: "Complete transaction history for each customer. Track dues effortlessly.",
    vendorManagementDesc: "Manage suppliers, track payables, and maintain healthy relationships.",
    profitReports: "Profit Reports",
    profitReportsDesc: "Item-wise profit analysis. Know exactly which products make you money.",
    everythingYouNeedTitle: "Everything You Need — Nothing You Don't",
    everythingYouNeedDescription: "We built FINO to be powerful yet simple. No confusing features, no unnecessary complexity.",
    smartFinancialTracking: "Smart Financial Tracking",
    smartFinancialTrackingDesc: "Track income, expenses, sales, and purchases with powerful analytics for both personal and business use.",
    powerfulAnalytics: "Powerful Analytics",
    powerfulAnalyticsDesc: "Get detailed insights with profit reports, sales trends, and spending patterns to make smarter decisions.",
    bankLevelSecurity: "Bank-Level Security",
    bankLevelSecurityDesc: "Your financial data is protected with enterprise-grade encryption. Your privacy is our top priority.",
    accessAnywhere: "Access Anywhere",
    accessAnywhereDesc: "Cloud sync keeps your data available on any device. Work from home, shop, or on the go.",
    testimonialsTitle: "Trusted by Businesses & Individuals",
    testimonialsDescription: "See why people love using FINO for their financial management",
    ctaTitle: "Ready to Take Control?",
    ctaDescription: "Join thousands of users who trust FINO for their personal and business finances. It's 100% free — no hidden charges, no premium plans.",
    startFreeToday: "Start Free Today",
    learnMoreAboutUs: "Learn More About Us",
  },

  // Features page
  features: {
    heroTag: "All Features — 100% Free",
    heroTitle: "Powerful Features for",
    heroTitleHighlight: "Complete Control",
    heroDescription: "FINO offers two modes — Cashbook for personal finance and IMS for business operations. Choose what fits your needs, or use both!",
    cashbookModeTitle: "Cashbook Mode",
    cashbookModeSubtitle: "Perfect for personal finance tracking",
    cashbookModeDescription: "Ideal for individuals, freelancers, and households. Track your income and expenses without any complexity. Simple, clean, and effective.",
    incomeExpenseTracking: "Income & Expense Tracking",
    incomeExpenseTrackingDesc: "Log your daily income and expenses with ease. Quick entry forms and categorization make tracking effortless.",
    multipleCashbooksTitle: "Multiple Cashbooks",
    multipleCashbooksDesc: "Create separate cashbooks for different purposes — personal, business, household, or projects. Keep everything organized.",
    smartAnalyticsTitle: "Smart Analytics",
    smartAnalyticsDesc: "Visualize your spending habits with interactive charts. Identify trends and make data-driven financial decisions.",
    budgetPlanning: "Budget Planning",
    budgetPlanningDesc: "Set budgets for different categories and track your progress. Stay within your spending limits effortlessly.",
    quickEntry: "Quick Entry",
    quickEntryDesc: "Add transactions in seconds with our streamlined interface. Designed for speed and simplicity.",
    detailedReports: "Detailed Reports",
    detailedReportsDesc: "Generate comprehensive financial reports for personal use or tax purposes. Export in multiple formats.",
    imsModeTitle: "IMS / POS Mode",
    imsModeSubtitle: "Inventory Management & Point of Sale",
    imsModeDescription: "Complete business solution for retail shops, wholesalers, and distributors. Manage your entire business — products, customers, vendors, invoices, and more — all in one place.",
    productCatalogTitle: "Product Catalog",
    productCatalogDesc: "Manage your complete product inventory with categories, SKUs, barcodes, and images. Track stock levels in real-time.",
    customerLedgerTitle: "Customer Ledger",
    customerLedgerDesc: "Maintain detailed ledgers for each customer. Track dues, payments, and complete transaction history. Never forget who owes you money.",
    vendorManagementTitle: "Vendor Management",
    vendorManagementDesc: "Manage your suppliers efficiently. Track payables, purchase history, and maintain healthy vendor relationships.",
    invoiceGenerationTitle: "Invoice Generation",
    invoiceGenerationDesc: "Create professional PDF invoices with your business logo. Share instantly via WhatsApp or email. Track payment status.",
    paymentRemindersTitle: "Payment Reminders",
    paymentRemindersDesc: "Set reminders for customer dues and vendor payments. Get notified before payment dates. Never miss collecting money again.",
    smartPricingTitle: "Smart Pricing",
    smartPricingDesc: "Auto rate suggestions based on your purchase history. Know your profit margins before making a sale. Make smarter pricing decisions.",
    batchTrackingTitle: "Batch Tracking",
    batchTrackingDesc: "Track products batch-by-batch with individual purchase rates. Know exactly which batch you're selling and at what cost.",
    postingQueue: "Posting Queue",
    postingQueueDesc: "Review and approve ledger entries before final posting. Prevent errors with a two-step verification process.",
    expiryAlertsTitle: "Expiry Alerts",
    expiryAlertsDesc: "Get notified before products expire. Configure alert days per product. No more losses from expired or near-expiry stock.",
    stockManagement: "Stock Management",
    stockManagementDesc: "Adjust stock for damages, returns, or miscounts. Track stock movements with detailed history. Maintain accurate inventory.",
    categoriesAndGroups: "Categories & Groups",
    categoriesAndGroupsDesc: "Organize products into categories. Group customers and vendors for better management and reporting.",
    profitReportsTitle: "Profit Reports",
    profitReportsDesc: "Item-wise profit analysis with cost basis calculation. Know exactly which products are making you money.",
    availableInBothModes: "Available in Both Modes",
    availableInBothModesDesc: "These core features are available regardless of which mode you choose. Security, sync, and convenience — built into everything we do.",
    cloudSync: "Cloud Sync",
    cloudSyncDesc: "Access your financial data from anywhere, anytime. Automatic cloud backup ensures you never lose your data.",
    secureStorage: "Secure Storage",
    secureStorageDesc: "Bank-level encryption protects your sensitive financial information. Your data security is our top priority.",
    privacyFirst: "Privacy First",
    privacyFirstDesc: "Your data belongs to you. We never share or sell your personal financial information to third parties.",
    recurringTransactions: "Recurring Transactions",
    recurringTransactionsDesc: "Automate recurring income and expenses. Set up subscriptions, salaries, and regular payments once.",
    multiUserSupport: "Multi-user Support",
    multiUserSupportDesc: "Perfect for teams and businesses. Share access with team members and collaborate on financial management.",
    smartNotifications: "Smart Notifications",
    smartNotificationsDesc: "Get alerts for unusual spending, bill reminders, and budget warnings to stay on top of your finances.",
    whyFreeTitle: "Why Is FINO Free?",
    whyFreeDescription: "We believe everyone deserves access to powerful financial tools. Running a small shop in Pakistan or managing household expenses — you shouldn't have to pay for basic financial management. That's why FINO is, and will always be, free for personal and small business use.",
    whyFreeSubtext: "No hidden charges. No premium plans. No feature locks. Just sign up and use everything.",
  },

  // Pricing page
  pricing: {
    heroTag: "Both Modes Included",
    heroTitle: "FINO Pricing —",
    heroTitleHighlight: "100% Free",
    heroSubtitle: "No Hidden Fees. No Limits. No Surprises.",
    heroDescription: "Whether you use Cashbook mode for personal finance or IMS mode for your business — everything is completely free. Forever.",
    cashbookMode: "Cashbook Mode",
    freeForever: "Free Forever",
    cashbookDescription: "Personal finance tracking for individuals, freelancers, and households.",
    imsMode: "IMS / POS Mode",
    imsDescription: "Complete business solution for shops, wholesalers, and distributors.",
    getStartedForFree: "Get Started for Free",
    noCreditCard: "No credit card required. Switch between modes anytime.",
    whyFreeTitle: "Why Is FINO Free?",
    whyFreeDescription: "We believe everyone deserves access to powerful financial tools. Running a small shop or managing household expenses — you should not have to pay for basic financial management. That is why FINO is, and will always be, free for personal and small business use.",
    futureUpdates: "Future Updates:",
    futureUpdatesDesc: "Any changes to pricing or features will be communicated well in advance. Your trust and transparency are important to us.",
    // Feature lists
    unlimitedTransactions: "Unlimited transactions",
    multipleCashbooks: "Multiple cashbooks",
    smartAnalyticsReports: "Smart analytics & reports",
    cloudSyncDevices: "Cloud sync across devices",
    budgetPlanning: "Budget planning",
    exportPdfExcel: "Export to PDF/Excel",
    unlimitedProducts: "Unlimited products & inventory",
    customerVendorLedgers: "Customer & vendor ledgers",
    invoiceWithLogo: "Invoice generation with logo",
    paymentReminders: "Payment reminders",
    smartPricingSuggestions: "Smart pricing suggestions",
    batchTrackingExpiry: "Batch tracking & expiry alerts",
    postingQueueSystem: "Posting queue system",
    profitLossReports: "Profit & loss reports",
  },

  // About page
  about: {
    heroTitle: "About",
    heroDescription: "Two powerful apps in one — personal finance tracking and complete business management, built for everyone.",
    ourStoryTitle: "Our Story",
    ourStoryP1: "FINO was born from a simple frustration: managing finances shouldn't be complicated. In 2023, our founder JABRAN ASIF struggled to find a simple yet powerful tool to track his business expenses without the complexity of traditional accounting software.",
    ourStoryP2: "What started as a personal project quickly grew into something bigger. Friends who ran small shops asked for inventory management. Family members wanted expense tracking. We listened to everyone — and built FINO to solve both problems.",
    ourStoryP3: "That's why FINO has two modes:",
    cashbookModeBullet: "Cashbook Mode — For individuals, freelancers, and households who want to track their daily income and expenses without any complexity.",
    imsModeBullet: "IMS Mode (Inventory Management System) — For shop owners, wholesalers, and distributors who need to manage products, customers, vendors, and invoices.",
    ourStoryP4: "Today, FINO helps thousands of individuals and businesses across Jeddah, KSA and beyond take control of their finances. Our mission remains unchanged: make financial management accessible, simple, and powerful for everyone —",
    completelyFree: "completely free",
    whoIsFinoFor: "Who Is FINO For?",
    whoIsFinoForDesc: "Whether you're managing household expenses or running a retail empire, FINO has a mode for you.",
    cashbookModeTitle: "Cashbook Mode",
    personalFinance: "Personal Finance",
    cashbookShortDesc: "Simple, clean, and focused on what matters — tracking your money.",
    individualsAndFamilies: "Individuals & Families",
    individualsDesc: "Track household expenses, plan budgets, and save more",
    freelancersAndConsultants: "Freelancers & Consultants",
    freelancersDesc: "Monitor income from multiple clients and track expenses",
    households: "Households",
    householdsDesc: "Manage family budgets and track every rupee spent",
    imsModeTitle: "IMS / POS Mode",
    businessManagement: "Business Management",
    imsShortDesc: "Complete business solution — products, customers, vendors, and invoices.",
    retailShops: "Retail Shops & Stores",
    retailDesc: "Manage inventory, create invoices, track customer dues",
    wholesalers: "Wholesalers & Distributors",
    wholesalersDesc: "Handle bulk inventory, vendor management, batch tracking",
    smbBusinesses: "Small & Medium Businesses",
    smbDesc: "Complete business operations with profit tracking",
    ourMission: "Our Mission",
    ourMissionDesc: "To empower everyone — from households to businesses — with simple, powerful, and free financial management tools.",
    ourVision: "Our Vision",
    ourVisionDesc: "A world where financial clarity is accessible to everyone, everywhere — regardless of the size of your wallet or business.",
    ourValues: "Our Values",
    ourValuesDesc: "Simplicity first, security always, and a customer-first approach in everything we build.",
    whyFreeTitle: "Why Is FINO Free?",
    whyFreeP1: "We believe that managing your money shouldn't cost you money. Whether you're a housewife tracking grocery expenses or a shop owner managing thousands of products — you deserve tools that work without burning a hole in your pocket.",
    whyFreeP2: "Most financial apps charge monthly fees or lock important features behind paywalls. We think that's unfair, especially for small businesses and individuals who need these tools the most.",
    whyFreeP3: "FINO is different. Everything is free. No premium plans. No hidden charges. No feature locks. Just sign up and use everything — Cashbook mode, IMS mode, all features, forever.",
    whyFreeTagline: "Because financial freedom should be free.",
    meetOurTeam: "Meet Our Team",
    meetOurTeamDesc: "The passionate people behind FINO",
    founderCeo: "Founder & CEO",
    coFounderCmo: "Co-Founder & CMO",
    coFounderCfo: "Co-Founder & CFO",
  },

  // Blog page
  blog: {
    heroTitle: "FINO",
    heroTitleHighlight: "Blog",
    heroDescription: "Tips for personal finance, inventory management, product updates, and insights to help you manage your money and business better",
    featured: "Featured",
    featuredTitle: "Two Modes, One Powerful App",
    featuredDescription: "FINO offers both Cashbook mode for personal finance and IMS mode for complete business management. Learn how to use each mode to take control of your finances.",
    exploreFeatures: "Explore all features",
    cashbook: "Cashbook",
    personalFinance: "Personal Finance",
    imsPOS: "IMS / POS",
    businessManagement: "Business Management",
    readMore: "Read more",
  },

  // Contact page
  contact: {
    heroTitle: "Get in",
    heroTitleHighlight: "Touch",
    heroDescription: "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    sendMessage: "Send us a Message",
    formDescription: "Fill out the form below and we'll get back to you within 24 hours",
    nameLabel: "Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    subjectLabel: "Subject",
    subjectPlaceholder: "How can we help?",
    messageLabel: "Message",
    messagePlaceholder: "Tell us more about your inquiry...",
    sendButton: "Send Message",
    sending: "Sending...",
    emailTitle: "Email",
    locationTitle: "Location",
    followUs: "Follow Us",
    messageSentTitle: "Message sent!",
    messageSentDesc: "We'll get back to you as soon as possible.",
    validationError: "Validation Error",
  },

  // Download page
  download: {
    heroTag: "Now Available on Google Play",
    heroTitle: "Download FINO Mobile App",
    heroDescription: "Manage your finances on the go with FINO's powerful mobile application. Fast, secure, and easy to use. Now officially released on the Google Play Store!",
    getOnGooglePlay: "Get FINO on Google Play",
    whyDownload: "Why Download FINO?",
    whyDownloadDesc: "Everything you need for financial management",
    lightningFast: "Lightning Fast",
    lightningFastDesc: "Optimized performance for smooth financial operations",
    bankLevelSecurity: "Bank-Level Security",
    bankLevelSecurityDesc: "Your data is encrypted and protected at all times",
    offlineAccess: "Offline Access",
    offlineAccessDesc: "Work without internet, sync when connected",
    realtimeSync: "Real-time Sync",
    realtimeSyncDesc: "Automatic synchronization across all your devices",
    dataBackup: "Data Backup",
    dataBackupDesc: "Never lose your financial records with cloud backup",
    multiBusiness: "Multi-Business",
    multiBusinessDesc: "Manage multiple businesses from a single app",
    howToInstall: "How to Install",
    howToInstallDesc: "Follow these simple steps",
    step1Title: "Enable Unknown Sources",
    step1Desc: "Go to Settings → Security → Enable 'Install from Unknown Sources' or 'Install Unknown Apps'",
    step2Title: "Download the APK",
    step2Desc: "Click the download button above to get the FINO APK file on your Android device",
    step3Title: "Install the App",
    step3Desc: "Open the downloaded APK file and tap 'Install'. Follow the on-screen prompts",
    step4Title: "Open and Sign In",
    step4Desc: "Launch FINO from your app drawer and sign in with your account credentials",
    systemRequirements: "System Requirements",
    systemRequirementsDesc: "Make sure your device is compatible",
    minimumRequirements: "Minimum Requirements",
    operatingSystem: "Operating System",
    operatingSystemValue: "Android 5.0 (Lollipop) or higher",
    storageSpace: "Storage Space",
    storageSpaceValue: "50 MB free space",
    ram: "RAM",
    ramValue: "1 GB or more",
    internetConnection: "Internet Connection",
    internetConnectionValue: "Required for sync (optional for offline use)",
    faq: "Frequently Asked Questions",
    faqDesc: "Everything you need to know",
    faq1Q: "Is it safe to install APK files?",
    faq1A: "Yes, FINO's APK is completely safe. We use bank-level security measures and the APK is signed with our official certificate. However, always download APKs only from official sources to ensure security.",
    faq2Q: "Why isn't FINO on the Google Play Store?",
    faq2A: "FINO is currently in beta testing phase. We're gathering user feedback and making improvements before the official Play Store launch. Direct APK distribution allows us to provide faster updates and new features.",
    faq3Q: "How do I update the app?",
    faq3A: "You'll receive an in-app notification when a new version is available. Simply download the latest APK from this page and install it over the existing version. Your data will be preserved during the update.",
    faq4Q: "What if installation fails?",
    faq4A: "If installation fails, ensure you've enabled \"Install from Unknown Sources\" in your device settings. Also, check that you have sufficient storage space. If problems persist, try restarting your device or contact our support team.",
    faq5Q: "Will my data sync between web and mobile?",
    faq5A: "Yes! FINO automatically syncs all your data across web and mobile platforms in real-time. Any changes you make on one device will instantly appear on all your other devices when connected to the internet.",
    faq6Q: "Can I use FINO offline?",
    faq6A: "Absolutely! FINO works offline and stores all your data locally. When you reconnect to the internet, all your offline changes will automatically sync to the cloud and across all your devices.",
    readyTitle: "Ready to Get Started?",
    readyDescription: "Download FINO now and take control of your finances anywhere, anytime.",
    downloadFromGooglePlay: "Download from Google Play",
  },

  // Auth page
  auth: {
    fino: "FINO",
    financialPlatform: "Your Financial Management Platform",
    welcome: "Welcome",
    signInOrSignUp: "Sign in to your account or create a new one",
    signIn: "Sign In",
    signUp: "Sign Up",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    confirmPassword: "Confirm Password",
    rememberMe: "Remember me on this device",
    signingIn: "Signing in...",
    signInButton: "Sign In",
    forgotPassword: "Forgot your password?",
    orContinueWith: "Or continue with",
    continueWithGoogle: "Continue with Google",
    fullName: "Full Name *",
    fullNamePlaceholder: "John Doe",
    password: "Password *",
    passwordStrength: "Password strength:",
    weak: "Weak",
    medium: "Medium",
    strong: "Strong",
    optionalInfo: "Optional Information",
    whatBringsYou: "What brings you here?",
    selectPurpose: "Select your purpose",
    manageBusiness: "Manage Business",
    householdExpenses: "Household Expenses",
    personalFinance: "Personal Finance",
    freelanceWork: "Freelance Work",
    other: "Other",
    country: "Country",
    countryPlaceholder: "Pakistan",
    city: "City",
    cityPlaceholder: "Karachi",
    phoneNumber: "Phone Number",
    phonePlaceholder: "+92 300 1234567",
    dateOfBirth: "Date of Birth",
    creatingAccount: "Creating account...",
    createAccount: "Create Account",
    uploadPhoto: "Upload Photo (Optional)",
    termsAgreement: "By continuing, you agree to our Terms of Service and Privacy Policy",
    verificationCodeSent: "Verification code sent",
    verificationCodeDesc: "Check your email for a 6-digit verification code",
    accountCreated: "Account created!",
    accountCreatedDesc: "Please verify your email. Check your inbox for a 6-digit verification code.",
  },

  // Settings page
  settings: {
    title: "Settings",
    subtitle: "Manage your account and preferences",
    profile: "Profile",
    security: "Security",
    preferences: "Preferences",
    prefs: "Prefs",
    backup: "Backup",
    personalInfo: "Personal Information",
    personalInfoDesc: "Update your personal details and preferences",
    dangerZone: "Danger Zone",
    dangerZoneDesc: "Irreversible actions that affect your account",
    deleteAccount: "Delete Account",
    deleteAccountDesc: "Permanently delete your account and all associated data",
    loadingSettings: "Loading settings...",
  },

  // Preferences
  preferences: {
    themeAndAppearance: "Theme & Appearance",
    themeAndAppearanceDesc: "Customize how the app looks and feels",
    theme: "Theme",
    light: "Light",
    dark: "Dark",
    system: "System",
    appMode: "App Mode",
    appModeDesc: "Choose between Simple Cashbook or Inventory Management System",
    currentMode: "Current Mode",
    cashbook: "Cashbook",
    ims: "IMS",
    imsDescription: "Advanced inventory management with products, customers, vendors & ledgers",
    cashbookDescription: "Simple income and expense tracking for your business",
    switchModeNote: "You can switch modes at any time. Your data will be preserved.",
    switchedToIms: "Switched to IMS Mode",
    switchedToCashbook: "Switched to Cashbook Mode",
    nowUsingIms: "Now using Inventory Management System",
    nowUsingCashbook: "Now using Simple Cashbook",
    preferenceUpdated: "Preference updated",
    businessSettings: "Business Settings",
    businessSettingsDesc: "Manage your business logo and information for invoices",
    businessLogo: "Business Logo",
    businessLogoUploadDesc: "Upload your business logo to appear on all invoices and ledger reports",
    uploading: "Uploading...",
    businessName: "Business Name",
    businessNamePlaceholder: "Enter your business name",
    contactInfo: "Contact Information",
    contactInfoPlaceholder: "Phone, email, address, etc.",
    contactInfoDesc: "This information will appear on invoices and reports",
    noOrganization: "No organization found",
    financialSettings: "Financial Settings",
    financialSettingsDesc: "Configure how numbers and dates are displayed",
    numberFormat: "Number Format",
    commaFormat: "Comma (1,234.56)",
    standardFormat: "Standard format",
    dotFormat: "Dot (1.234,56)",
    europeanFormat: "European format",
    spaceFormat: "Space (1 234.56)",
    internationalFormat: "International format",
    dateFormat: "Date Format",
    securitySettings: "Security Settings",
    securitySettingsDesc: "Manage your account security preferences",
    autoLogoutTimer: "Auto-logout Timer",
    minutes15: "15 minutes",
    minutes30: "30 minutes",
    hour1: "1 hour",
    hours2: "2 hours",
    never: "Never",
    autoLogoutDesc: "Automatically sign out after period of inactivity",
    stayLoggedIn: "Stay Logged In",
    stayLoggedInDesc: "Keep me logged in across browser sessions",
    postingChanges: "Posting Changes",
    postingChangesDesc: "Control how changes are posted to the ledger",
    postingChangesQuestion: "Do you want to check and repost every change in the data?",
    checkAndPost: "Yes, I want to check and post the changes",
    checkAndPostDesc: "Review all changes before they are posted to the ledger",
    directToLedger: "No, post directly to ledger",
    directToLedgerDesc: "Changes are automatically posted without review",
    transactionDefaults: "Transaction Defaults",
    transactionDefaultsDesc: "Set default values for new transactions",
    defaultTransactionType: "Default Transaction Type",
    cashIn: "Cash In (Income)",
    cashOut: "Cash Out (Expense)",
    defaultPaymentMethod: "Default Payment Method",
    cash: "Cash",
    online: "Online",
    language: "Language",
    languageDesc: "Change the app language",
    english: "English",
    arabic: "العربية (Arabic)",
  },

  // Module Selection
  moduleSelection: {
    badge: "Business Management Ecosystem",
    title: "Welcome to FINO",
    subtitle: "Choose your module to get started. Switch between modules anytime from your dashboard.",
    cashbook: "Cashbook",
    cashbookDesc: "Track your daily income and expenses with ease",
    cashbookFeature1: "Record cash-in and cash-out transactions",
    cashbookFeature2: "Multiple cashbooks for different businesses",
    cashbookFeature3: "Generate monthly reports and summaries",
    cashbookFeature4: "Export data to Excel/PDF",
    openCashbook: "Open Cashbook",
    inventoryManagement: "Inventory Management",
    inventoryDesc: "Complete business management system",
    imsFeature1: "Manage products, categories, and stock",
    imsFeature2: "Vendors & customers management",
    imsFeature3: "Create invoices and track payments",
    imsFeature4: "Detailed ledgers and financial reports",
    openInventory: "Open Inventory",
    opening: "Opening...",
    footerNote: "You can access both modules anytime. Your data is securely stored and synced across all devices.",
    modeSelected: "Mode Selected",
    welcomeToMode: "Welcome to {mode} mode!",
  },

  // Inventory / IMS
  inventory: {
    title: "Inventory Management",
    dashboard: "Dashboard",
    products: "Products",
    stockManagement: "Stock Management",
    customers: "Customers",
    vendors: "Vendors",
    analytics: "Analytics",
    reports: "Reports",
    reminders: "Reminders",
    history: "History",
    management: "Management",
    goodMorning: "Good morning",
    goodAfternoon: "Good afternoon",
    goodEvening: "Good evening",
    totalSales: "Total Sales",
    totalProfit: "Total Profit",
    receivables: "Receivables",
    payables: "Payables",
    stockValue: "Stock Value",
    netPosition: "Net Position",
    totalProducts: "Total Products",
    pendingInvoices: "Pending Invoices",
    lowStockAlert: "Low Stock Alert",
    cashInHand: "Cash In Hand",
    manageInfo: "Manage your customer information",
    manageSupplierInfo: "Manage your supplier information",
    activityHistory: "Activity History",
    activityHistoryDesc: "Review all actions across products, customers, and vendors",
    productActivity: "View product activity including sales, purchases, and stock adjustments",
    customerActivity: "Track customer transactions, invoices, payments, and returns",
    vendorActivity: "Monitor vendor activities, purchases, payments, and returns",
    recentActivities: "recent activities",
    viewHistory: "View history",
    historyTip: "Click on any category above to view detailed history with date filters, action types, and export options.",
    tip: "Tip:",
    analyticsDesc: "Comprehensive business insights and performance metrics",
    cropBusinessLogo: "Crop Business Logo",
    addTransaction: "Add Transaction",
    transactionHistory: "Transaction history and financial overview",
    totalCashIn: "Total Cash In",
    totalCashOut: "Total Cash Out",
    netBalance: "Net Balance",
    recentTransactions: "Recent Transactions",
    searchTransactions: "Search transactions...",
    noTransactions: "No transactions yet",
    startAddingTransaction: "Start by adding your first transaction",
    addFirstTransaction: "Add First Transaction",
    noTransactionsFound: "No transactions found",
    adjustSearchOrFilters: "Try adjusting your search or filters",
    clearFilters: "Clear Filters",
    activeFilters: "Active filters:",
    clearAll: "Clear all",
    filterTransactions: "Filter Transactions",
    transactionType: "Transaction Type",
    allTypes: "All Types",
    cashIn: "Cash In",
    cashOut: "Cash Out",
    paymentMethod: "Payment Method",
    allMethods: "All Methods",
    applyFilters: "Apply Filters",
    finalBalance: "Final Balance",
  },

  // Business List (Cashbook)
  businessList: {
    myBusinesses: "My Businesses",
    subtitle: "Manage your business portfolio and financial records",
    addBusiness: "Add Business",
    add: "Add",
    noBusinesses: "No businesses yet",
    noBusinessesDesc: "Create your first business to start managing finances",
    addFirstBusiness: "Add Your First Business",
    deleteBusiness: "Delete Business",
    deleteConfirm: "Are you sure you want to delete \"{name}\"? This action cannot be undone and will also delete all associated cashbooks and transactions.",
  },

  // Cashbook List
  cashbookList: {
    manageCashbooks: "Manage cashbooks and financial records",
    addCashbook: "Add Cashbook",
    noCashbooks: "No cashbooks yet",
    noCashbooksDesc: "Create your first cashbook to start recording transactions",
    addFirstCashbook: "Add Your First Cashbook",
    deleteCashbook: "Delete Cashbook",
    deleteConfirm: "Are you sure you want to delete \"{name}\"? This action cannot be undone and will also delete all associated transactions.",
  },

  // Footer
  footer: {
    quickLinks: "Quick Links",
    support: "Support",
    helpCenter: "Help Center",
    faq: "FAQ",
    privacyPolicy: "Privacy Policy",
    termsAndConditions: "Terms & Conditions",
    connectWithUs: "Connect With Us",
    emailUs: "We'd love to hear from you!",
    logoutConfirmTitle: "Log out and go to home page?",
    logoutConfirmDesc: "You will be logged out of your account and redirected to the home page.",
    yesLogout: "Yes, log out",
  },

  // 404 page
  notFound: {
    title: "404",
    description: "Oops! Page not found",
    returnHome: "Return to Home",
  },

  // Privacy page
  privacy: {
    title: "Privacy Policy",
    intro: "Your privacy is important to us. This Privacy Policy explains how FINO collects, uses, and protects your information.",
  },

  // Terms page
  terms: {
    title: "Terms & Conditions",
    intro: "Welcome to FINO! By downloading, accessing, or using our application and services, you agree to comply with and be bound by the following Terms & Conditions. Please read them carefully.",
  },
};

export type TranslationKeys = typeof en;
